import { useCallback, useEffect, useMemo, useState, type KeyboardEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Crosshair, FileSpreadsheet, Globe2, ListTree, Map, Plus, RefreshCw,
} from 'lucide-react'
import {
  ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, SortTh, StatusBadge, Td, Th,
} from '@/components/erp/primitives'
import { ActiveFilters, FilterBar, MultiSelect, SearchInput } from '@/shared/ui/filters'
import { OffsetPager } from '@/shared/ui/CursorPager'
import { EmptyState, ErrorStrip, Forbidden, LoadingRows, Note } from '@/shared/ui/states'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { explain } from '@/shared/api/problem'
import { fmtNumber } from '@/shared/lib/format'
import { countActive } from '@/shared/lib/query'
import { cn } from '@/lib/utils'
import { createCountry, geographySummary, getUnit, listUnits, type UnitFilters } from './api'
import { UnitTree } from './UnitTree'
import { UnitDetailDrawer } from './UnitDetailDrawer'
import { ChangeStatusDialog, EditUnitDialog } from './UnitDialogs'
import { CreateUnitDialog, CreatedTrail } from './CreateUnitDialog'
import { NearbyDialog } from './NearbyDialog'
import { ImportWizard } from './ImportWizard'
import { MapView } from './MapView'
import { AgentCell, MoodCell, RiskBadge, TurnoutCell } from './IntelCells'
import {
  RISK_LABEL, RISK_ORDER, UNIT_TYPE_LABEL, UNIT_TYPE_ORDER, childTypeOf,
  type UnitDetailResponse, type UnitResponse,
} from '@/shared/api/types'

const STATUSES = ['ACTIVE', 'PROPOSED', 'MERGED', 'DISSOLVED']

/** Table columns, so the colSpan of the empty/loading rows cannot drift from the header. */
const COLUMN_COUNT = 11

/**
 * "Polling station" wraps to two lines in a column this narrow and doubles every
 * station row's height. The full label is on the row's own detail pane and in the
 * type filter, so the table uses the short form.
 */
const UNIT_TYPE_SHORT: Record<string, string> = {
  COUNTRY: 'Country',
  COUNTY: 'County',
  CONSTITUENCY: 'Constituency',
  WARD: 'Ward',
  POLLING_STATION: 'Station',
}

const VIEWS = [
  { id: 'explorer', label: 'Explorer', icon: ListTree },
  { id: 'map', label: 'Map', icon: Map },
  { id: 'import', label: 'Import', icon: FileSpreadsheet },
] as const

/**
 * Geography & Electoral Units.
 *
 * Explorer is master/detail: a lazy tree on the left for "where is this in the
 * hierarchy", a filtered table on the right for "find me every ward called X".
 * Both feed the same detail drawer. The tree cannot answer a search and the
 * table cannot show containment, so both earn their place.
 *
 * Everything is scope-filtered server-side — a county-scoped coordinator sees
 * their county's subtree and nothing else, and that is the backend's decision,
 * not a client filter.
 */
export function GeographyPage() {
  const canRead = useCan('geography:read')
  const canCreate = useCan('geography:create')

  const [params, setParams] = useSearchParams()
  const raw = params.get('view')
  const view = raw && VIEWS.some((v) => v.id === raw) ? raw : 'explorer'

  const setView = (id: string) => {
    const sp = new URLSearchParams(params)
    sp.set('view', id)
    setParams(sp, { replace: true })
  }

  /** Clicking a unit on the map hands off to the explorer with its drawer open. */
  const openUnit = (id: string) => {
    const sp = new URLSearchParams(params)
    sp.set('view', 'explorer')
    sp.set('unit', id)
    setParams(sp, { replace: false })
  }

  if (!canRead) return <Forbidden what="electoral units" />

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Geography & Electoral Units"
        description="Counties, constituencies, wards and polling stations. Every other module is scoped to a unit here."
      />

      <div className="border-b border-border">
        <div className="flex items-center gap-1 overflow-x-auto">
          {VIEWS.map((v) => {
            const Icon = v.icon
            return (
              <button
                key={v.id}
                onClick={() => setView(v.id)}
                className={cn(
                  'h-9 px-4 text-erp-sm border-b-2 -mb-px inline-flex items-center gap-2 whitespace-nowrap',
                  view === v.id
                    ? 'border-chocolate text-chocolate font-semibold'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {v.label}
              </button>
            )
          })}
        </div>
      </div>

      {view === 'explorer' ? <Explorer canCreate={canCreate} />
        : view === 'map' ? <MapView onOpenUnit={openUnit} />
        : <ImportWizard />}
    </div>
  )
}

function Explorer({ canCreate }: { canCreate: boolean }) {
  const [params, setParams] = useSearchParams()
  const [page, setPage] = useState(0)
  const [selectedId, setSelectedId] = useState<string | null>(params.get('unit'))
  /**
   * Selection and the detail drawer are separate on purpose. They used to be the
   * same thing, which made "Add child" unreachable: it only renders with a unit
   * selected, closing the drawer cleared the selection, and while the drawer was
   * open its overlay covered the button. Now closing the drawer keeps the row
   * selected, so the toolbar action stays available.
   */
  const [detailOpen, setDetailOpen] = useState(Boolean(params.get('unit')))
  const [creatingUnder, setCreatingUnder] = useState<UnitResponse | null>(null)
  const [editing, setEditing] = useState<UnitDetailResponse | null>(null)
  const [statusing, setStatusing] = useState<UnitDetailResponse | null>(null)
  const [nearbyOpen, setNearbyOpen] = useState(false)
  /** Units created in one "keep open to add another" run, shown above the table. */
  const [trail, setTrail] = useState<UnitResponse[]>([])
  const [bootstrapping, setBootstrapping] = useState(false)
  const [note, setNote] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null)

  const filters = useMemo<UnitFilters>(() => ({
    q: params.get('q') ?? '',
    type: params.getAll('type'),
    status: params.getAll('status'),
    parentId: params.get('parentId') ?? '',
    region: params.get('region') ?? '',
    sort: params.get('sort') ?? '',
    riskLevel: params.getAll('riskLevel'),
    hasAgent: params.get('hasAgent') ?? '',
    assessed: params.get('assessed') ?? '',
    turnoutMin: params.get('turnoutMin') ?? '',
    turnoutMax: params.get('turnoutMax') ?? '',
    moodMin: params.get('moodMin') ?? '',
    moodMax: params.get('moodMax') ?? '',
  }), [params])

  const [draftQ, setDraftQ] = useState(filters.q)
  const debouncedQ = useDebounced(draftQ, 300)
  useEffect(() => { setDraftQ(filters.q) }, [filters.q])

  const patch = useCallback((next: Partial<UnitFilters>) => {
    const m = { ...filters, ...next }
    const sp = new URLSearchParams(params)
    sp.delete('type'); sp.delete('status'); sp.delete('riskLevel')
    m.type.forEach((v) => sp.append('type', v))
    m.status.forEach((v) => sp.append('status', v))
    m.riskLevel.forEach((v) => sp.append('riskLevel', v))
    const setOrDrop = (k: string, v: string) => { if (v) sp.set(k, v); else sp.delete(k) }
    setOrDrop('q', m.q)
    setOrDrop('parentId', m.parentId)
    setOrDrop('region', m.region)
    setOrDrop('sort', m.sort)
    setOrDrop('hasAgent', m.hasAgent)
    setOrDrop('assessed', m.assessed)
    setOrDrop('turnoutMin', m.turnoutMin)
    setOrDrop('turnoutMax', m.turnoutMax)
    setOrDrop('moodMin', m.moodMin)
    setOrDrop('moodMax', m.moodMax)
    setParams(sp, { replace: true })
    setPage(0)
  }, [filters, params, setParams])

  useEffect(() => {
    if (debouncedQ !== filters.q) patch({ q: debouncedQ })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ])

  const select = useCallback((id: string | null) => {
    setSelectedId(id)
    setDetailOpen(Boolean(id))
    const sp = new URLSearchParams(params)
    if (id) sp.set('unit', id); else sp.delete('unit')
    setParams(sp, { replace: true })
  }, [params, setParams])

  const [sortField, sortDir] = (filters.sort || 'name,asc').split(',') as [string, string]
  const toggleSort = (field: string) => {
    // Clicking the active column flips direction; another starts ascending. Every
    // overlay sort puts nulls last in BOTH directions server-side, so "not assessed"
    // never occupies the first page of an ascending risk sort.
    patch({ sort: `${field},${sortField === field && sortDir === 'asc' ? 'desc' : 'asc'}` })
  }

  const filterKey = JSON.stringify(filters)
  const list = useLoad(() => listUnits(filters, page, 50), [filterKey, page])
  const summary = useLoad(() => geographySummary(), [])
  const [treeKey, setTreeKey] = useState(0)

  const refreshAll = () => {
    list.reload()
    summary.reload()
    setTreeKey((k) => k + 1)   // remounts the tree so cached branches refetch
  }

  const activeCount = countActive({
    q: filters.q, type: filters.type, status: filters.status,
    parentId: filters.parentId, region: filters.region,
    riskLevel: filters.riskLevel, hasAgent: filters.hasAgent,
    assessed: filters.assessed, turnoutMin: filters.turnoutMin, turnoutMax: filters.turnoutMax,
    moodMin: filters.moodMin, moodMax: filters.moodMax,
  })

  const clearAll = () => {
    setDraftQ('')
    const sp = new URLSearchParams(params)
    for (const k of ['q', 'type', 'status', 'parentId', 'region', 'riskLevel', 'hasAgent',
                     'assessed', 'turnoutMin', 'turnoutMax', 'moodMin', 'moodMax']) sp.delete(k)
    setParams(sp, { replace: true })
    setPage(0)
  }

  async function bootstrapCountry() {
    setBootstrapping(true)
    setNote(null)
    try {
      const country = await createCountry()
      setNote({ tone: 'good', text: `Created the root unit “${country.name}”. Import counties next.` })
      refreshAll()
    } catch (err) {
      setNote({ tone: 'bad', text: explain(err) })
    } finally {
      setBootstrapping(false)
    }
  }

  /** Open the create dialog beneath whatever unit is currently selected. */
  async function addUnder(unit: UnitResponse | null) {
    if (unit) { setCreatingUnder(unit); return }
    if (!selectedId) return
    try {
      const d = await getUnit(selectedId)
      setCreatingUnder(d.unit)
    } catch (err) {
      setNote({ tone: 'bad', text: explain(err) })
    }
  }

  const s = summary.data
  const rows = list.data?.content ?? []
  const total = s?.total ?? 0
  const empty = total === 0 && !summary.loading && activeCount === 0

  /**
   * How much of the scope has been assessed at all. Driven by the summary rather
   * than the current page, so filtering to the four assessed wards does not make
   * the banner claim everything is covered.
   */
  const unassessed = s?.byRisk?.UNASSESSED ?? 0
  const nothingAssessed = total > 0 && unassessed === total

  return (
    <div className="space-y-4">
      {note && <Note tone={note.tone} onDismiss={() => setNote(null)}>{note.text}</Note>}
      <CreatedTrail items={trail} />

      {/* The system genuinely starts with nothing — say so, and offer the way in. */}
      {empty ? (
        <Panel>
          <EmptyState
            icon={<Globe2 className="h-4 w-4" />}
            title="No electoral units yet"
            hint="The hierarchy is real data with provenance, not seed data — it is loaded from an IEBC file. Create the country root first, then import counties, constituencies, wards and stations in that order."
            action={canCreate ? (
              <button className="erp-btn erp-btn-primary" onClick={bootstrapCountry} disabled={bootstrapping}>
                <Plus className="h-3.5 w-3.5" /> Create the country root
              </button>
            ) : undefined}
          />
        </Panel>
      ) : (
        <>
          <KpiStrip>
            <MetricCard
              label="Total units"
              value={s ? fmtNumber(s.total) : '—'}
              sub="within your scope"
              accent
            />
            {UNIT_TYPE_ORDER.filter((t) => t !== 'COUNTRY').map((t) => (
              <MetricCard
                key={t}
                label={UNIT_TYPE_LABEL[t] ?? t}
                value={s ? fmtNumber(s.byType[t] ?? 0) : '—'}
                sub={t === 'POLLING_STATION' ? 'the bottom of the tree' : undefined}
              />
            ))}
          </KpiStrip>

          {/*
            Coverage, not counts. Three numbers that answer "how much of this do we
            actually know?" — deliberately a thin strip rather than three more KPI
            cards, because they are context for the table below, not headlines.
          */}
          {s && (s.byRisk || s.agentCoverage || s.turnoutCoverage) && (
            <div className="flex items-center gap-x-6 gap-y-1.5 flex-wrap text-erp-xs text-muted-foreground">
              {s.byRisk && (
                <span>
                  Assessed{' '}
                  <span className="erp-metric text-foreground">
                    {fmtNumber(total - (s.byRisk.UNASSESSED ?? 0))}
                  </span>
                  {' '}of {fmtNumber(total)}
                  {RISK_ORDER.filter((r) => s.byRisk?.[r]).map((r) => (
                    <span key={r} className="ml-2">
                      {RISK_LABEL[r]} <span className="erp-metric text-foreground">
                        {fmtNumber(s.byRisk?.[r] ?? 0)}
                      </span>
                    </span>
                  ))}
                </span>
              )}
              {s.turnoutCoverage && (
                <span>
                  Turnout recorded for{' '}
                  <span className="erp-metric text-foreground">
                    {fmtNumber(s.turnoutCoverage.unitsWithTurnout)}
                  </span>
                  {' '}of {fmtNumber(s.turnoutCoverage.unitsTotal)}
                </span>
              )}
              {s.agentCoverage && s.agentCoverage.stationsTotal > 0 && (
                <span>
                  Agents at{' '}
                  <span className="erp-metric text-foreground">
                    {fmtNumber(s.agentCoverage.stationsWithAgent)}
                  </span>
                  {' '}of {fmtNumber(s.agentCoverage.stationsTotal)} stations
                </span>
              )}
            </div>
          )}

          {s && (s.byStatus.MERGED || s.byStatus.DISSOLVED) ? (
            <p className="text-erp-xs text-muted-foreground">
              {fmtNumber((s.byStatus.MERGED ?? 0) + (s.byStatus.DISSOLVED ?? 0))} unit(s) are merged
              or dissolved. They are kept because historical records still reference them, and are
              excluded from rollups.
            </p>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] items-start">
            {/* Master — the hierarchy */}
            <Panel
              title="Hierarchy"
              description="Loads one level at a time"
              bodyClassName="p-0"
              className="min-w-0"
              right={
                <button className="erp-btn" onClick={() => setTreeKey((k) => k + 1)} title="Reload tree">
                  <RefreshCw className="h-3.5 w-3.5" />
                </button>
              }
            >
              <div className="max-h-[30rem] overflow-y-auto">
                <UnitTree key={treeKey} selectedId={selectedId ?? undefined} onSelect={select} />
              </div>
            </Panel>

            {/* Detail — the searchable flat list */}
            <div className="min-w-0 space-y-4">
              <FilterBar
                activeCount={activeCount}
                right={
                  <>
                    <ActiveFilters count={activeCount} onClear={clearAll} />
                    <button className="erp-btn" onClick={() => setNearbyOpen(true)} aria-label="Nearby">
                      <Crosshair className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Nearby</span>
                    </button>
                    <button className="erp-btn" onClick={refreshAll} aria-label="Refresh">
                      <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
                    </button>
                    {canCreate && selectedId && (
                      <button className="erp-btn erp-btn-primary" onClick={() => addUnder(null)} aria-label="Add child">
                        <Plus className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Add child</span>
                      </button>
                    )}
                  </>
                }
              >
                <SearchInput value={draftQ} onChange={setDraftQ} placeholder="Search name or code…" />
                <MultiSelect
                  label="Type"
                  options={UNIT_TYPE_ORDER.map((t) => ({
                    value: t, label: UNIT_TYPE_LABEL[t] ?? t, count: s?.byType[t],
                  }))}
                  selected={filters.type}
                  onChange={(type) => patch({ type })}
                />
                <MultiSelect
                  label="Status"
                  options={STATUSES.map((v) => ({
                    value: v,
                    label: v.charAt(0) + v.slice(1).toLowerCase(),
                    count: s?.byStatus[v],
                  }))}
                  selected={filters.status}
                  onChange={(status) => patch({ status })}
                />
                <>
                    <MultiSelect
                      label="Risk"
                      options={RISK_ORDER.map((v) => ({
                        value: v, label: RISK_LABEL[v] ?? v, count: s?.byRisk?.[v],
                      }))}
                      selected={filters.riskLevel}
                      onChange={(riskLevel) => patch({ riskLevel })}
                    />
                    <select
                      value={filters.hasAgent}
                      onChange={(e) => patch({
                        hasAgent: e.target.value,
                        // An agent belongs to a station; above that the column is
                        // coverage. The backend now restricts `hasAgent` to stations
                        // itself, so any other type filter would return an empty
                        // page — pin it rather than showing nothing with no reason.
                        type: e.target.value ? ['POLLING_STATION'] : filters.type,
                      })}
                      className="erp-input"
                      aria-label="Agent"
                    >
                      <option value="">Agent: any</option>
                      <option value="true">Has an agent</option>
                      <option value="false">No agent</option>
                    </select>
                    <select
                      value={filters.assessed}
                      onChange={(e) => patch({ assessed: e.target.value })}
                      className="erp-input"
                      aria-label="Assessment"
                    >
                      <option value="">Assessment: any</option>
                      <option value="true">Assessed</option>
                      <option value="false">Not assessed</option>
                    </select>
                    <RangeFilter
                      label="Turnout %"
                      min={filters.turnoutMin}
                      max={filters.turnoutMax}
                      onChange={(turnoutMin, turnoutMax) => patch({ turnoutMin, turnoutMax })}
                    />
                    <RangeFilter
                      label="Mood"
                      min={filters.moodMin}
                      max={filters.moodMax}
                      onChange={(moodMin, moodMax) => patch({ moodMin, moodMax })}
                    />
                  </>
              </FilterBar>

              {filters.parentId && (
                <Note tone="info" onDismiss={() => patch({ parentId: '' })}>
                  Showing direct children of one unit only.
                </Note>
              )}

              {nothingAssessed && (
                <p className="text-erp-xs text-muted-foreground">
                  Turnout, mood, risk and agent are all “—” because nothing has been
                  assessed yet. Record election statistics on a unit to fill turnout,
                  and publish mood, risk or an agent from its detail pane.
                </p>
              )}

              <Panel bodyClassName="p-0">
                <ErrorStrip message={list.error} />
                <ErpTable
                  head={
                    <>
                      <SortTh field="name" label="Name" active={sortField} dir={sortDir} onSort={toggleSort} />
                      <SortTh field="code" label="Code" active={sortField} dir={sortDir} onSort={toggleSort} />
                      <Th className="axis-hide-sm">Type</Th>
                      <Th className="axis-hide-lg">Parent</Th>
                      <Th>Voters</Th>
                      <Th className="axis-hide-md">Stations</Th>
                      <SortTh field="turnout" label="Turnout" active={sortField} dir={sortDir}
                              onSort={toggleSort} className="axis-hide-md" />
                      <SortTh field="mood" label="Mood" active={sortField} dir={sortDir}
                              onSort={toggleSort} className="axis-hide-md" />
                      <SortTh field="risk" label="Risk" active={sortField} dir={sortDir}
                              onSort={toggleSort}
                              title="Sorts by severity, not alphabetically. Unassessed units sort last either way." />
                      <SortTh field="agentCoverage" label="Agent" active={sortField} dir={sortDir}
                              onSort={toggleSort} className="axis-hide-md" />
                      <Th className="axis-hide-sm">Status</Th>
                    </>
                  }
                >
                  {list.loading && rows.length === 0 ? (
                    <LoadingRows colSpan={COLUMN_COUNT} />
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={COLUMN_COUNT}>
                        <EmptyState
                          icon={<Map className="h-4 w-4" />}
                          title="No units match this filter"
                          hint="Clear a filter, or import the level you are looking for."
                          action={activeCount > 0
                            ? <button className="erp-btn" onClick={clearAll}>Clear filters</button>
                            : undefined}
                        />
                      </td>
                    </tr>
                  ) : (
                    rows.map((u) => (
                      <tr
                        key={u.id}
                        onClick={() => select(u.id)}
                        className={cn(
                          'hover:bg-muted/40 cursor-pointer',
                          u.id === selectedId && 'bg-chocolate-soft/30',
                        )}
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') select(u.id) }}
                      >
                        <Td>
                          <span className={cn('truncate', u.status !== 'ACTIVE' && 'text-muted-foreground')}>
                            {u.name}
                          </span>
                          {/* Below md most columns are hidden, so the row carries its
                              own summary: code, type, and whichever intelligence
                              values exist. Otherwise a phone shows a name and a
                              number and nothing that made this screen worth opening. */}
                          <div className="md:hidden text-erp-xs text-muted-foreground font-mono">
                            {u.code} · {UNIT_TYPE_SHORT[u.unitType] ?? u.unitType}
                            {u.overlay?.turnoutPct != null && ` · ${u.overlay.turnoutPct.toFixed(1)}%`}
                            {u.overlay?.moodIndex != null && ` · mood ${Math.round(u.overlay.moodIndex)}`}
                          </div>
                        </Td>
                        <Td className="font-mono text-erp-xs">{u.code}</Td>
                        <Td className="axis-hide-sm whitespace-nowrap">
                          {UNIT_TYPE_SHORT[u.unitType] ?? UNIT_TYPE_LABEL[u.unitType] ?? u.unitType}
                        </Td>
                        <Td className="axis-hide-lg truncate max-w-[10rem]">{u.parentName ?? '—'}</Td>
                        <Td className="erp-metric">{fmtNumber(u.registeredVoters)}</Td>
                        <Td className="axis-hide-md erp-metric">
                          {u.unitType === 'POLLING_STATION' ? '—' : fmtNumber(u.stationCount)}
                        </Td>
                        <Td className="axis-hide-md"><TurnoutCell overlay={u.overlay} /></Td>
                        <Td className="axis-hide-md"><MoodCell overlay={u.overlay} /></Td>
                        <Td><RiskBadge level={u.overlay?.riskLevel} /></Td>
                        <Td className="axis-hide-md max-w-[10rem]"><AgentCell unit={u} /></Td>
                        <Td className="axis-hide-sm"><StatusBadge status={u.status} /></Td>
                      </tr>
                    ))
                  )}
                </ErpTable>

                <OffsetPager
                  page={page}
                  totalPages={list.data?.totalPages ?? 1}
                  totalElements={list.data?.totalElements ?? 0}
                  onPage={setPage}
                  loading={list.loading}
                  unit="units"
                />
              </Panel>
            </div>
          </div>
        </>
      )}

      <UnitDetailDrawer
        unitId={detailOpen ? selectedId : null}
        onClose={() => setDetailOpen(false)}
        onEdit={setEditing}
        onChangeStatus={setStatusing}
        onNavigate={select}
        onChanged={refreshAll}
        onAddChild={canCreate ? (u) => { setDetailOpen(false); setCreatingUnder(u) } : undefined}
      />
      <CreateUnitDialog
        open={Boolean(creatingUnder)}
        parent={creatingUnder}
        onClose={() => { setCreatingUnder(null); setTrail([]) }}
        onCreated={(u: UnitResponse, keepOpen: boolean) => {
          refreshAll()
          if (keepOpen) {
            setTrail((t) => [...t, u])
            return
          }
          select(u.id)
          setNote({
            tone: 'good',
            text: `Created ${u.name}. ${childTypeOf(u.unitType)
              ? `You can now add ${UNIT_TYPE_LABEL[childTypeOf(u.unitType)!]?.toLowerCase()}s beneath it.`
              : ''}`,
          })
        }}
      />
      <EditUnitDialog detail={editing} onClose={() => setEditing(null)} onSaved={refreshAll} />
      <ChangeStatusDialog detail={statusing} onClose={() => setStatusing(null)} onSaved={refreshAll} />
      <NearbyDialog open={nearbyOpen} onClose={() => setNearbyOpen(false)} onPick={select} />
    </div>
  )
}

/**
 * A numeric min/max pair as one filter control.
 *
 * Debounced by commit rather than by keystroke: typing "7" on the way to "70"
 * would otherwise fire a request for everything above 7%. The value is applied on
 * blur or Enter, which is also what stops the URL churning on every digit.
 */
function RangeFilter({
  label, min, max, onChange,
}: {
  label: string
  min: string
  max: string
  onChange: (min: string, max: string) => void
}) {
  const [lo, setLo] = useState(min)
  const [hi, setHi] = useState(max)
  useEffect(() => { setLo(min); setHi(max) }, [min, max])

  const commit = () => { if (lo !== min || hi !== max) onChange(lo, hi) }
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); commit() }
  }

  return (
    <div className="inline-flex items-center gap-1.5">
      <span className="text-erp-xs text-muted-foreground whitespace-nowrap">{label}</span>
      <input
        type="number" inputMode="numeric" min={0} max={100}
        value={lo} onChange={(e) => setLo(e.target.value)} onBlur={commit} onKeyDown={onKey}
        className="erp-input w-16" placeholder="min" aria-label={`${label} minimum`}
      />
      <span className="text-muted-foreground">–</span>
      <input
        type="number" inputMode="numeric" min={0} max={100}
        value={hi} onChange={(e) => setHi(e.target.value)} onBlur={commit} onKeyDown={onKey}
        className="erp-input w-16" placeholder="max" aria-label={`${label} maximum`}
      />
    </div>
  )
}
